import React, { useState, useEffect, useRef } from 'react';
import { Bot, User, Info, Send, X, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { NutritionJournal } from '@/types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useRecipes } from '@/contexts/RecipesContext';
import { useNavigate } from 'react-router-dom';
import ChatMessageList from "./chat/ChatMessageList";
import ChatInput from "./chat/ChatInput";
import { ChatAlert } from "./chat/ChatAlert";

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
}

interface ChatWidgetProps {
  onClose: () => void;
}

const ChatWidget: React.FC<ChatWidgetProps> = ({ onClose }) => {
  const { user } = useAuth();
  const { recipes } = useRecipes();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [apiKeyError, setApiKeyError] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState<boolean>(false);
  const [nutritionData, setNutritionData] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [requestTimeout, setRequestTimeout] = useState<number | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [requestInProgress, setRequestInProgress] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const MAX_RETRIES = 2;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    
    loadNutritionData();
    loadUserProfile();
    loadStoredMessages();
    loadSavedMessages();

    return () => {
      if (requestTimeout) {
        clearTimeout(requestTimeout);
      }
    };
  }, []);
  
  const loadStoredMessages = () => {
    if (!user) return;
    
    try {
      const storageKey = `lisa_chat_${user.id}`;
      const storedMessages = localStorage.getItem(storageKey);
      
      if (storedMessages) {
        const parsedMessages = JSON.parse(storedMessages);
        
        const formattedMessages = parsedMessages.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
        
        setMessages(formattedMessages);
        console.log('Loaded stored chat messages:', formattedMessages.length);
      } else {
        createPersonalizedGreeting();
      }
    } catch (error) {
      console.error('Error loading stored chat messages:', error);
      createPersonalizedGreeting();
    }
  };

  const createPersonalizedGreeting = () => {
    const userName = user?.name || '';
    const time = new Date().getHours();
    let greeting = '';
    
    if (time < 12) {
      greeting = `¡Buenos días ${userName}! 🌞 Soy Lisa, tu guía vegana personal. ¿En qué puedo ayudarte hoy?`;
    } else if (time < 18) {
      greeting = `¡Hola ${userName}! 🌱 Soy Lisa, tu guía vegana personal. ¿Qué tal va tu día? ¿En qué puedo ayudarte?`;
    } else {
      greeting = `¡Buenas noches ${userName}! ✨ Soy Lisa, tu guía vegana personal. ¿En qué puedo ayudarte?`;
    }
    
    const initialMessage: Message = {
      id: '1',
      content: greeting,
      isUser: false,
      timestamp: new Date(),
    };
    
    setMessages([initialMessage]);
  };

  const loadSavedMessages = async () => {
    if (!user) return;
    
    try {
      const storageKey = `lisa_chat_${user.id}`;
      const storedMessages = localStorage.getItem(storageKey);
      
      if (!storedMessages) {
        const { data, error } = await supabase
          .from('lisa_conversations')
          .select('*')
          .eq('user_id', user.id)
          .order('timestamp', { ascending: true })
          .limit(50);
        
        if (error) {
          console.error('Error loading conversations from database:', error);
          return;
        }
        
        if (data && data.length > 0) {
          const dbMessages: Message[] = data.map(msg => ({
            id: msg.id,
            content: msg.message,
            isUser: msg.is_user,
            timestamp: new Date(msg.timestamp)
          }));
          
          setMessages(dbMessages);
          console.log('Loaded database chat messages:', dbMessages.length);
          
          const messagesToStore = dbMessages.map(msg => ({
            ...msg,
            timestamp: msg.timestamp.toISOString()
          }));
          localStorage.setItem(storageKey, JSON.stringify(messagesToStore));
        }
      }
    } catch (error) {
      console.error('Error loading messages from database:', error);
    }
  };
  
  const loadNutritionData = async () => {
    if (!user) return;
    
    try {
      console.log('Loading user nutritional data');
      
      const currentDate = new Date();
      const dateString = format(currentDate, 'yyyy-MM-dd');
      const userId = user.id || 'no-user';
      
      const journalKey = `journal_${userId}_${dateString}`;
      const storedJournal = localStorage.getItem(journalKey);
      
      if (storedJournal) {
        const journalData: NutritionJournal = JSON.parse(storedJournal);
        console.log('Nutritional data loaded:', journalData);
        
        setNutritionData({
          ...journalData.nutritionSummary,
          steps: journalData.steps,
          workout: journalData.workout,
          weight: journalData.weight,
          waterIntake: journalData.waterIntake,
          sunExposure: journalData.sunExposure,
          restLevel: journalData.restLevel,
          energyLevel: journalData.energyLevel,
          stressLevel: journalData.stressLevel,
          bodyFat: journalData.bodyFat,
          bodyWater: journalData.bodyWater,
          muscleMass: journalData.muscleMass,
          meals: journalData.meals
        });
      } else {
        console.log('No nutritional data found for today');
        
        const yesterdayDate = new Date(currentDate);
        yesterdayDate.setDate(yesterdayDate.getDate() - 1);
        const yesterdayDateString = format(yesterdayDate, 'yyyy-MM-dd');
        const yesterdayJournalKey = `journal_${userId}_${yesterdayDateString}`;
        
        const yesterdayJournal = localStorage.getItem(yesterdayJournalKey);
        if (yesterdayJournal) {
          console.log('Using nutritional data from yesterday');
          const journalData: NutritionJournal = JSON.parse(yesterdayJournal);
          
          setNutritionData({
            ...journalData.nutritionSummary,
            steps: journalData.steps,
            workout: journalData.workout,
            weight: journalData.weight,
            waterIntake: journalData.waterIntake,
            sunExposure: journalData.sunExposure,
            restLevel: journalData.restLevel,
            energyLevel: journalData.energyLevel,
            stressLevel: journalData.stressLevel,
            bodyFat: journalData.bodyFat,
            bodyWater: journalData.bodyWater,
            muscleMass: journalData.muscleMass,
            meals: journalData.meals
          });
        }
      }
    } catch (error) {
      console.error('Error loading nutritional data:', error);
    }
  };
  
  const loadUserProfile = async () => {
    if (!user) return;
    
    try {
      console.log('Loading user profile');
      
      const { data, error } = await supabase
        .from('profiles')
        .select('name, username, objectives, description')
        .eq('id', user.id)
        .single();
        
      if (error) {
        console.error('Error loading user profile:', error);
        return;
      }
      
      if (data) {
        console.log('User profile loaded:', data);
        setUserProfile(data);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const saveMessageToDatabase = async (message: Message, isUser: boolean) => {
    if (!user) return;
    
    try {
      // Primero verificamos si tenemos acceso a la tabla de conversaciones
      console.log('Trying to save message to database:', message.content.substring(0, 30) + '...');
      
      const { error: permissionCheckError } = await supabase
        .from('lisa_conversations')
        .select('id', { count: 'exact', head: true })
        .limit(1);
      
      if (permissionCheckError) {
        console.error('Error checking permissions for conversations table:', permissionCheckError);
        return; // Error silencioso pero registrado
      }
      
      // Ahora intentamos insertar el mensaje
      const { data, error } = await supabase
        .from('lisa_conversations')
        .insert({
          user_id: user.id,
          message: message.content,
          is_user: isUser,
          timestamp: message.timestamp.toISOString(),
          metadata: {}
        })
        .select('id');
      
      if (error) {
        console.error('Error saving message to database:', error);
      } else {
        console.log('Message successfully saved to database with ID:', data?.[0]?.id);
        
        // Actualizar el localStorage después de guardar en la base de datos
        const storageKey = `lisa_chat_${user.id}`;
        const storedMessages = localStorage.getItem(storageKey);
        if (storedMessages) {
          const parsedMessages = JSON.parse(storedMessages);
          const updatedMessages = [...parsedMessages, {
            ...message,
            timestamp: message.timestamp.toISOString()
          }];
          localStorage.setItem(storageKey, JSON.stringify(updatedMessages));
        }
      }
    } catch (error) {
      console.error('Error in saveMessageToDatabase:', error);
    }
  };

  const handleConnectionError = () => {
    setConnectionError(true);
    setIsLoading(false);
    setHasError(true);
    window.dispatchEvent(new CustomEvent('lisa-connection-error'));
    
    const errorMessage: Message = {
      id: (Date.now() + 1).toString(),
      content: 'Hubo un problema con la conexión. Intenta más tarde.',
      isUser: false,
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, errorMessage]);
    saveMessageToDatabase(errorMessage, false);
    toast.error('Error de conexión. Intenta de nuevo más tarde.');
  };

  const updateMessageInProgressStatus = (status: boolean) => {
    window.dispatchEvent(new CustomEvent('lisa-message-in-progress', {
      detail: { status }
    }));
    setIsSending(status);
  };

  const handleMessageClick = (content: string) => {
    if (content.startsWith('/receta/')) {
      const recipeId = content.replace('/receta/', '');
      
      console.log('Navigating to recipe:', recipeId);
      navigate(`/receta/${recipeId}`);
      onClose();
      toast.success('Navegando a la receta');
    } else if (content.includes('/retos')) {
      navigate('/retos');
      onClose();
      toast.success('Navegando a tus retos');
    } else {
      console.log('No actionable link found in message content:', content);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading || requestInProgress) {
      console.log('Send blocked:', { 
        emptyInput: !inputValue.trim(), 
        isLoading, 
        requestInProgress
      });
      return;
    }
    
    setRequestInProgress(true);
    updateMessageInProgressStatus(true);
    
    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue,
      isUser: true,
      timestamp: new Date(),
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setHasError(false);
    setApiKeyError(null);
    setConnectionError(false);
    
    try {
      await saveMessageToDatabase(userMessage, true);
      
      // Actualizar el localStorage también
      const storageKey = `lisa_chat_${user?.id}`;
      const storedMessages = localStorage.getItem(storageKey);
      if (storedMessages) {
        const parsedMessages = JSON.parse(storedMessages);
        const updatedMessages = [...parsedMessages, {
          ...userMessage,
          timestamp: userMessage.timestamp.toISOString()
        }];
        localStorage.setItem(storageKey, JSON.stringify(updatedMessages));
      } else {
        // Inicializar el localStorage con este mensaje
        localStorage.setItem(storageKey, JSON.stringify([{
          ...userMessage,
          timestamp: userMessage.timestamp.toISOString()
        }]));
      }
    } catch (saveError) {
      console.error('Failed to save user message to database:', saveError);
    }
    
    const timeout = window.setTimeout(() => {
      if (isLoading) {
        console.warn('Request to Lisa exceeded timeout');
        setIsLoading(false);
        setHasError(true);
        updateMessageInProgressStatus(false);
        setRequestInProgress(false);
        
        const timeoutMessage: Message = {
          id: (Date.now() + 1).toString(),
          content: 'Lo siento, estoy tardando demasiado en responder. ¿Podrías intentarlo de nuevo? 😓',
          isUser: false,
          timestamp: new Date(),
        };
        
        setMessages(prev => [...prev, timeoutMessage]);
        try {
          saveMessageToDatabase(timeoutMessage, false);
        } catch (error) {
          console.error('Failed to save timeout message:', error);
        }
        toast.error('Tiempo de espera agotado. Intenta de nuevo.');
      }
    }, 30000);
    
    setRequestTimeout(timeout);
    
    try {
      console.log('Sending message:', inputValue);
      
      const functionUrl = `${import.meta.env.VITE_SUPABASE_URL || 'https://fxjyyqdntzslxnqfcelf.supabase.co'}/functions/v1/chat-with-lisa`;
      const apiKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImZ4anl5cWRudHpzbHhucWZjZWxmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDM2NzMxNTMsImV4cCI6MjA1OTI0OTE1M30.Dv1MFUrcW-YWIUpzsyXHb65El0VnM4kEQvbf_RZvDcI';
      
      const pastMessages = messages.slice(-6);
      
      let weeklyMenuData = null;
      let shoppingListData = null;
      let nutritionHistoryData = null;
      let allRecipesData = recipes || [];
      
      // Enhanced recipe data processing to ensure complete recipe information
      console.log(`Sending ${allRecipesData.length} recipes to Lisa AI assistant`);
      
      try {
        const weeklyMenuString = localStorage.getItem(`weeklyMenu_${user?.id}_${format(new Date(), 'yyyy-MM-dd')}`);
        if (weeklyMenuString) {
          const menuData = JSON.parse(weeklyMenuString);
          
          const urlParams = new URLSearchParams(window.location.search);
          const currentRoute = window.location.pathname;
          const isOnMenuPage = currentRoute.includes('/menu-semanal');
          
          if (isOnMenuPage && menuData) {
            const selectedDayParam = urlParams.get('day') || 'monday';
            weeklyMenuData = {
              ...menuData,
              selectedDay: selectedDayParam,
              dayTotals: menuData.days && menuData.days[selectedDayParam] ? menuData.days[selectedDayParam].totals || {} : {}
            };
            
            console.log('Sending weekly menu data with selected day:', selectedDayParam);
          } else {
            weeklyMenuData = menuData;
          }
        }
        
        const shoppingListString = localStorage.getItem(`shoppingList_${user?.id}_${format(new Date(), 'yyyy-MM-dd')}`);
        if (shoppingListString) {
          shoppingListData = JSON.parse(shoppingListString);
        }
        
        const lastWeek = [];
        const today = new Date();
        for (let i = 0; i < 7; i++) {
          const date = new Date(today);
          date.setDate(date.getDate() - i);
          const dateString = format(date, 'yyyy-MM-dd');
          const journalKey = `journal_${user?.id}_${dateString}`;
          const journalString = localStorage.getItem(journalKey);
          if (journalString) {
            const journal = JSON.parse(journalString);
            lastWeek.push({
              date: dateString,
              nutritionSummary: journal.nutritionSummary || {}
            });
          }
        }
        nutritionHistoryData = lastWeek;
        
      } catch (error) {
        console.error('Error obtaining data for Lisa:', error);
      }
      
      // Include all recipe data (with full ingredient details) in the request
      const functionCall = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({ 
          message: inputValue,
          userId: user?.id || 'anonymous',
          nutritionData: nutritionData,
          userProfile: userProfile,
          pastMessages: pastMessages,
          recipesData: allRecipesData,
          weeklyMenu: weeklyMenuData,
          shoppingList: shoppingListData,
          nutritionHistory: nutritionHistoryData
        })
      });
      
      if (!functionCall.ok) {
        const errorText = await functionCall.text();
        console.error('Function call error status:', functionCall.status, errorText);
        throw new Error(`Function returned error status: ${functionCall.status}`);
      }
      
      const data = await functionCall.json();

      if (requestTimeout) {
        clearTimeout(requestTimeout);
        setRequestTimeout(null);
      }

      console.log('Function response:', data);

      if (!data) {
        console.error('Invalid response (no data):', data);
        throw new Error('No valid response received');
      }
      
      if (data.error) {
        console.error('Error in response:', data.error);
        
        if (data.requiresConfig) {
          setApiKeyError(data.error);
          throw new Error(data.error || 'API configuration error');
        } else {
          throw new Error(data.error || 'Error processing your message');
        }
      }

      if (!data.response) {
        console.error('Response without content:', data);
        throw new Error('No valid response received');
      }

      setRetryCount(0);

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: data.response,
        isUser: false,
        timestamp: new Date(),
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      
      try {
        await saveMessageToDatabase(assistantMessage, false);
        
        // Actualizar el localStorage también con el mensaje de respuesta
        const storageKey = `lisa_chat_${user?.id}`;
        const storedMessages = localStorage.getItem(storageKey);
        if (storedMessages) {
          const parsedMessages = JSON.parse(storedMessages);
          const updatedMessages = [...parsedMessages, {
            ...assistantMessage,
            timestamp: assistantMessage.timestamp.toISOString()
          }];
          localStorage.setItem(storageKey, JSON.stringify(updatedMessages));
        } else {
          // Inicializar el localStorage con este mensaje
          localStorage.setItem(storageKey, JSON.stringify([{
            ...assistantMessage,
            timestamp: assistantMessage.timestamp.toISOString()
          }]));
        }
      } catch (saveError) {
        console.error('Failed to save Lisa response to database:', saveError);
      }
      
    } catch (error) {
      if (requestTimeout) {
        clearTimeout(requestTimeout);
        setRequestTimeout(null);
      }
      
      console.error('Error getting response:', error);
      
      setHasError(true);
      
      if (retryCount >= MAX_RETRIES || apiKeyError) {
        const errorMessage = apiKeyError 
          ? `Error de configuración: ${apiKeyError}` 
          : 'Lo siento, tuve un problema al procesar tu mensaje. Por favor, intenta más tarde.';
        
        const errorResponse: Message = {
          id: (Date.now() + 1).toString(),
          content: errorMessage,
          isUser: false,
          timestamp: new Date(),
        };
        
        setMessages(prev => [...prev, errorResponse]);
        
        try {
          await saveMessageToDatabase(errorResponse, false);
        } catch (saveError) {
          console.error('Failed to save error message to database:', saveError);
        }
        
        toast.error('Error comunicándose con el asistente');
      } else {
        setRetryCount(prev => prev + 1);
      }
    } finally {
      setIsLoading(false);
      updateMessageInProgressStatus(false);
      setRequestInProgress(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleRetry = () => {
    setConnectionError(false);
    setHasError(false);
    setRetryCount(0);
    setRequestInProgress(false);
    
    if (messages.length > 1) {
      const lastUserMessage = [...messages].reverse().find(m => m.isUser);
      if (lastUserMessage) {
        setInputValue(lastUserMessage.content);
      }
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-xl w-80 sm:w-96 flex flex-col overflow-hidden border border-gray-200">
      <div className="bg-vegana-primary text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Avatar className="h-8 w-8 border-2 border-white">
            <AvatarImage src="/lovable-uploads/ae745547-332f-46cf-8369-8992e8b453d4.png" alt="Lisa" />
            <AvatarFallback>L</AvatarFallback>
          </Avatar>
          <div>
            <h3 className="font-medium text-sm">Lisa</h3>
            <p className="text-xs text-white/80">Tu guía vegana</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-white hover:bg-white/20" 
            onClick={onClose}
            disabled={requestInProgress}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="p-4 h-96 overflow-y-auto bg-gray-50 flex flex-col space-y-4">
        <Alert className="bg-amber-50 border-amber-200">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-xs text-amber-700">
            Lisa puede cometer errores. Para consultas importantes, comunícate con tu nutricionista en la sección de soporte.
          </AlertDescription>
        </Alert>
        <ChatMessageList 
          messages={messages}
          user={user}
          onMessageClick={handleMessageClick}
        />
        <ChatAlert
          apiKeyError={apiKeyError}
          connectionError={connectionError}
          hasError={hasError && !apiKeyError && !connectionError}
          onRetry={handleRetry}
        />
        {nutritionData && (
          <div className="text-center mt-2 mb-1">
            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
              Lisa tiene acceso a tu información nutricional completa, perfil, recetas y retos
            </span>
          </div>
        )}
      </div>
      <ChatInput
        inputValue={inputValue}
        setInputValue={setInputValue}
        onSendMessage={handleSendMessage}
        isLoading={isLoading}
        requestInProgress={requestInProgress}
        inputRef={inputRef}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
};

export default ChatWidget;
